import styled from "styled-components";
import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import { baseUrl } from "../config";
import { DateIRLToErache } from "../utils/dateConverters";
import { FaSortUp, FaSortDown } from "react-icons/fa";
import { Table, ListContainer } from "./styles";
import PeriodsList from "./PeriodsList";
import DetailDrawer from "./DetailDrawer";

const PERIODS_COLUMNS_STORAGE_KEY = "chronofield.columns.all-periods";
const DEFAULT_PERIODS_COLUMN_VISIBILITY = {
  startDate: false,
  endDate: false,
  startEventName: false,
  endEventName: false,
  rpDescription: false,
  hrpDescription: false,
};

const loadPeriodColumnVisibility = () => {
  try {
    const raw = localStorage.getItem(PERIODS_COLUMNS_STORAGE_KEY);
    if (!raw) return DEFAULT_PERIODS_COLUMN_VISIBILITY;
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) {
      return DEFAULT_PERIODS_COLUMN_VISIBILITY;
    }
    return { ...DEFAULT_PERIODS_COLUMN_VISIBILITY, ...parsed };
  } catch {
    return DEFAULT_PERIODS_COLUMN_VISIBILITY;
  }
};

const AllPeriodsList = () => {
  const [openDetail, setOpenDetail] = useState(null);
  const [periods, setPeriods] = useState([]);
  const [sortConfig, setSortConfig] = useState({ key: "startDate", direction: "asc" });
  const [pagination, setPagination] = useState({ pageIndex: 0, pageSize: 20 });
  const [rowCount, setRowCount] = useState(0);
  const [columnFilters, setColumnFilters] = useState([]);
  const [columnVisibility, setColumnVisibility] = useState(loadPeriodColumnVisibility);

  useEffect(() => {
    localStorage.setItem(PERIODS_COLUMNS_STORAGE_KEY, JSON.stringify(columnVisibility));
  }, [columnVisibility]);

  // Prevent out-of-order responses overwriting a newer page.
  const fetchSeqRef = useRef(0);
  const fetchPeriods = async (pageIndex, pageSize, sortKey, sortDirection, columnFilters) => {
    const fetchSeq = ++fetchSeqRef.current;
    try {
      const params = {
        page: pageIndex + 1,
        pageSize,
        sortKey: sortKey || "startDate",
        sortDirection: sortDirection || "asc",
      };
      if (columnFilters && columnFilters.length) {
        columnFilters.forEach((f) => {
          params[f.id] = f.value;
        });
      }
      const response = await axios.get(baseUrl + "/periods", {
        withCredentials: true,
        params,
      });
      // Ignore stale responses.
      if (fetchSeq !== fetchSeqRef.current) return;
      setPeriods(response.data.data || []);
      setRowCount(response.data.total || 0);
    } catch (err) {
      console.error("Error fetching periods:", err);
    }
  };

  useEffect(() => {
    fetchPeriods(
      pagination.pageIndex,
      pagination.pageSize,
      sortConfig.key,
      sortConfig.direction,
      columnFilters
    );
  }, [
    pagination.pageIndex,
    pagination.pageSize,
    sortConfig.key,
    sortConfig.direction,
    columnFilters,
  ]);

  const requestSort = (key) => {
    const direction =
      sortConfig.key === key && sortConfig.direction === "asc" ? "desc" : "asc";
    setSortConfig({ key, direction });
  };

  const refetchPeriods = () => {
    fetchPeriods(pagination.pageIndex, pagination.pageSize, sortConfig.key, sortConfig.direction, columnFilters);
  };

  return (
    <AllPeriodsListContainer>
      <Title>Liste des périodes</Title>
      <PeriodsList
        fetchPeriods={fetchPeriods}
        periods={periods}
        pagination={pagination}
        setPagination={setPagination}
        rowCount={rowCount}
        columnFilters={columnFilters}
        setColumnFilters={setColumnFilters}
        columnVisibility={columnVisibility}
        setColumnVisibility={setColumnVisibility}
        requestSort={requestSort}
        sortConfig={sortConfig}
        setSortConfig={setSortConfig}
        DateIRLToErache={DateIRLToErache}
        FaSortUp={FaSortUp}
        FaSortDown={FaSortDown}
        ListContainer={ListContainer}
        Table={Table}
        onOpenEventDetail={(id) => setOpenDetail({ type: "event", id })}
        onOpenPeriodDetail={(id) => setOpenDetail({ type: "period", id })}
      />
      <DetailDrawer
        open={openDetail}
        onClose={() => setOpenDetail(null)}
        onOpenDetail={setOpenDetail}
        onEventDeleted={refetchPeriods}
        onPeriodDeleted={refetchPeriods}
        DateIRLToErache={DateIRLToErache}
      />
    </AllPeriodsListContainer>
  );
};

export default AllPeriodsList;

const Title = styled.h1`
  margin-bottom: var(--space-4);
`;

const AllPeriodsListContainer = styled(ListContainer)`
  width: 100vw;
  max-width: 100vw;
  margin: 0 auto;
  padding-top: var(--space-10);
`;
